import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ensureTranslations,
  readCachedTranslation,
} from '../../firebase/translations';

/**
 * Resolves the viewer-facing title for a product in a list/card context.
 *
 * When translation is needed but not yet available (no cache, fetch in
 * progress), returns an empty string so the caller can show a skeleton
 * placeholder. Once the translation arrives (or fails), the appropriate
 * text is shown.
 *
 * @param {Object} product product doc (may be null/undefined)
 * @returns {{name: string, loading: boolean}}
 */
export function useTranslatedName(product) {
  const { i18n } = useTranslation();
  const lang = i18n?.language || 'en';
  const originalLanguage = product?.originalLanguage || 'bg';
  const needsTranslation = Boolean(
    product && product.id && lang && lang !== originalLanguage
  );

  // Start with cached translation if available; empty if fetch needed
  const compute = () => {
    if (!product) return { name: '', loading: false };
    if (!needsTranslation) return { name: product.name || '', loading: false };
    const cached = readCachedTranslation(product, lang);
    if (cached && cached.name) return { name: cached.name, loading: false };
    // Need to fetch — start empty so caller shows placeholder
    return { name: '', loading: true };
  };

  const [state, setState] = useState(compute);

  useEffect(() => {
    if (!product) {
      setState({ name: '', loading: false });
      return;
    }
    if (!needsTranslation) {
      setState({ name: product.name || '', loading: false });
      return;
    }
    const cached = readCachedTranslation(product, lang);
    if (cached && cached.name) {
      setState({ name: cached.name, loading: false });
      return;
    }
    // Show placeholder while fetching
    setState({ name: '', loading: true });
    let cancelled = false;
    ensureTranslations([product.id], lang)
      .then((res) => {
        if (cancelled) return;
        const entry = res && res[product.id];
        if (entry && entry.name) {
          setState({ name: entry.name, loading: false });
        } else {
          // Translation failed — show original as fallback
          setState({ name: product.name || '', loading: false });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // On error, show original text
        setState({ name: product.name || '', loading: false });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, lang, needsTranslation]);

  return state;
}
